/**
 * The four moments (D19 phase 18): the logo on the sign-in page, an empty can where a list is empty, a milk drop while
 * the ERP is reading, and a tick that draws itself when a save lands. Each is a few lines of SVG moved by CSS on the
 * motion tokens — no animation library and no supplied art was needed. Under "Reduce motion" (Appearance, or the
 * operating system) they stand still; in the classic look they are not shown at all (ADR-0011 is unchanged).
 */
import { useEffect, useRef, useState } from 'react';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';

/** The RTCOM mark, with a ring that draws round it once. */
export function LogoMoment({ size = 56 }: { size?: number }) {
  return (
    <span className="moment moment-logo relative inline-flex shrink-0 items-center justify-center" style={{ width: size, height: size }}>
      <svg viewBox="0 0 56 56" className="absolute inset-0" aria-hidden>
        <circle cx="28" cy="28" r="26" fill="none" stroke="var(--color-brand)" strokeWidth="2" strokeLinecap="round" pathLength={100} className="moment-ring" />
      </svg>
      <img src="/brand/rtcom-logo-160.png" alt="RTCOM" width={size - 14} height={size - 14} className="moment-logo-img" />
    </span>
  );
}

/** An empty milk can, gently swaying: nothing here yet. */
export function EmptyCan() {
  return (
    <svg viewBox="0 0 64 64" width={56} height={56} className="moment moment-empty text-ink-faint" aria-hidden>
      <ellipse cx="32" cy="58" rx="16" ry="3" fill="currentColor" opacity="0.18" />
      <g className="moment-sway">
        <path d="M22 14h20l-2 6v30a6 6 0 0 1-6 6h-4a6 6 0 0 1-6-6V20z" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
        <path d="M20 10h24v4H20z" fill="currentColor" opacity="0.5" />
        <path d="M26 30h12" stroke="var(--color-brand)" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
      </g>
    </svg>
  );
}

/** A milk drop falling into its ripple: the ERP is reading. */
export function MilkDrop({ size = 18, label = 'Reading' }: { size?: number; label?: string }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className="moment moment-drop" role="img" aria-label={label}>
      <path className="moment-drop-fall" d="M12 3c2.6 3.4 4 5.8 4 7.6A4 4 0 0 1 8 10.6C8 8.8 9.4 6.4 12 3z" fill="var(--color-brand)" />
      <ellipse className="moment-drop-ripple" cx="12" cy="20" rx="6" ry="1.6" fill="none" stroke="var(--color-brand)" strokeWidth="1.4" />
    </svg>
  );
}

/** A tick that draws itself: what was saved has landed. */
export function SyncedTick({ size = 18 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className="moment moment-tick" role="img" aria-label="Saved">
      <circle cx="12" cy="12" r="10" fill="var(--color-accepted, #2f7d4f)" opacity="0.14" />
      <path d="M7 12.5l3.2 3.2L17 9" fill="none" stroke="var(--color-accepted, #2f7d4f)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" pathLength={100} className="moment-tick-path" />
    </svg>
  );
}

/** The top bar's quiet signal: a drop while anything is read or saved, a tick for a moment after a save. */
export function ActivityMoment() {
  const fetching = useIsFetching();
  const mutating = useIsMutating();
  const wasMutating = useRef(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (mutating > 0) { wasMutating.current = true; return; }
    if (!wasMutating.current) return;
    wasMutating.current = false;
    setSaved(true);
    const t = window.setTimeout(() => setSaved(false), 1600);
    return () => window.clearTimeout(t);
  }, [mutating]);
  return (
    <span className="moment-slot inline-flex w-5 items-center justify-center" aria-live="polite">
      {fetching + mutating > 0 ? <MilkDrop /> : saved ? <SyncedTick /> : null}
    </span>
  );
}
